import { Anchor, Button, Checkbox, Group, Loader, SegmentedControl, Stack, Text, Textarea, TextInput } from '@mantine/core'
import { useDocumentTitle } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import {
  IconArrowLeft,
  IconBuildingStore,
  IconCheck,
  IconClockOff,
  IconDownload,
  IconEye,
  IconFileOff,
  IconPencil,
  IconPhone,
  IconPrinter,
  IconReceipt,
} from '@tabler/icons-react'
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query'
import { AreaInput, companyQueryKey, isApiError, usePrint } from '@tnn/app-kit'
import { formatDate, formatMoney, formatPhone, type ThaiArea } from '@tnn/core'
import { CustomerReceiptDocument } from '@tnn/documents'
import { type SelfReceiptInput, selfReceiptInput } from '@tnn/domain'
import { currentLocale } from '@tnn/i18n'
import { EmptyState, InitialsAvatar } from '@tnn/ui'
import { type ReactNode, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { receiptApi, type SelfReceiptPage } from './api'
import { savePdf } from './pdf'
import { ScaledPage } from './sheet'
import classes from './sign.module.css'

/**
 * `/receipt/<code>/<payment>`, opened from a bill's tracking page: a company that paid the bill's freight issues the
 * receipt in its name itself, no account (modules/tms/public-receipts.ts). It fills in its registered name, tax ID, head
 * office or branch and address (what the book knows of the payer filled in first), checks the receipt as it will print,
 * ticks that it paid, and issues it at once; then keeps it as a PDF, here again whenever the QR is scanned. A payment
 * that does not allow it says why and which branch to call.
 */

const r = (key: string) => `sign:receipt.${key}`
const HEAD_OFFICE = '00000'

/** The company's query, answered from what the page sent, for the receipt as it prints */
function answered(data: SelfReceiptPage) {
  const client = new QueryClient({
    defaultOptions: { queries: { staleTime: Number.POSITIVE_INFINITY, retry: false, refetchOnWindowFocus: false, enabled: false } },
  })
  client.setQueryData(companyQueryKey, data.company)
  return client
}

/**
 * The bill's tracking page on the company website, which opened this one: its address from where the visitor came
 * (browsers send another site's origin only), as the website's host is not this one's less `sign.` everywhere
 * (www.stg.thananan.co on staging); none when opened from elsewhere
 */
function trackPage(code: string) {
  try {
    const from = new URL(document.referrer).origin
    return from === window.location.origin ? null : `${from}/track/${code}`
  } catch {
    return null
  }
}

interface Values {
  name: string
  taxId: string
  headOffice: boolean
  branchNo: string
  address: string
  area: ThaiArea
}

const initial = (found: SelfReceiptPage['defaults']): Values => ({
  name: found?.name ?? '',
  taxId: found?.taxId ?? '',
  headOffice: !found?.branch || found.branch === HEAD_OFFICE,
  branchNo: found?.branch && found.branch !== HEAD_OFFICE ? found.branch : '',
  address: found?.address ?? '',
  area: {
    subdistrict: found?.subdistrict ?? '',
    district: found?.district ?? '',
    province: found?.province ?? '',
    postcode: found?.postcode ?? '',
  },
})

/** What is sent: a branch number padded to five digits, head office 00000 */
const inputOf = (values: Values) => ({
  name: values.name,
  taxId: values.taxId.replace(/\D/g, ''),
  branch: values.headOffice ? HEAD_OFFICE : values.branchNo.trim().padStart(5, '0'),
  address: values.address,
  subdistrict: values.area.subdistrict ?? '',
  district: values.area.district ?? '',
  province: values.area.province ?? '',
  postcode: values.area.postcode ?? '',
})

type Errors = Partial<Record<'name' | 'taxId' | 'branch' | 'address' | 'area', string>>
const areaFields = ['subdistrict', 'district', 'province', 'postcode']

/** Field errors by the form's boxes: the four parts of a place are one box */
function errorsOf(issues: readonly { path: readonly PropertyKey[]; message: string }[]): Errors {
  const errors: Errors = {}
  for (const issue of issues) {
    const field = String(issue.path[0] ?? '')
    const box = (areaFields.includes(field) ? 'area' : field) as keyof Errors
    errors[box] ??= issue.message
  }
  return errors
}

/** The branch that took the money, to call */
function CallBranch({ data }: { data: SelfReceiptPage }) {
  const { t } = useTranslation()
  if (!data.branch.phone) return null
  return (
    <Button
      component="a"
      href={`tel:${data.branch.phone.split(/[,;/]/)[0]?.replace(/[^\d+]/g, '')}`}
      size="md"
      radius="xl"
      variant="default"
      leftSection={<IconPhone size={18} />}
    >
      {t(r('call'), { branch: data.branch.name })} {formatPhone(data.branch.phone)}
    </Button>
  )
}

/** The company's name and tax ID, head office or branch, and address, checked as the API checks them */
function ReceiptForm({
  values,
  onChange,
  errors,
  onReview,
}: {
  values: Values
  onChange: (next: Partial<Values>) => void
  errors: Errors
  onReview: () => void
}) {
  const { t } = useTranslation()
  return (
    <section className={classes.card}>
      <Stack gap={4}>
        <h2 className={classes.cardTitle}>{t(r('formTitle'))}</h2>
        <Text size="sm" c="dimmed">
          {t(r('formHint'))}
        </Text>
      </Stack>
      <TextInput
        size="md"
        label={t(r('name'))}
        placeholder={t(r('namePlaceholder'))}
        withAsterisk
        autoComplete="organization"
        value={values.name}
        error={errors.name}
        onChange={(e) => onChange({ name: e.currentTarget.value })}
      />
      <TextInput
        size="md"
        label={t(r('taxId'))}
        withAsterisk
        inputMode="numeric"
        maxLength={17}
        value={values.taxId}
        error={errors.taxId}
        onChange={(e) => onChange({ taxId: e.currentTarget.value })}
      />
      <Stack gap={8}>
        <SegmentedControl
          size="md"
          radius="xl"
          fullWidth
          value={values.headOffice ? 'head' : 'branch'}
          data={[
            { value: 'head', label: t(r('headOffice')) },
            { value: 'branch', label: t(r('branch')) },
          ]}
          onChange={(value) => onChange({ headOffice: value === 'head' })}
        />
        {!values.headOffice && (
          <TextInput
            size="md"
            label={t(r('branchNo'))}
            withAsterisk
            inputMode="numeric"
            maxLength={5}
            value={values.branchNo}
            error={errors.branch}
            onChange={(e) => onChange({ branchNo: e.currentTarget.value.replace(/\D/g, '') })}
          />
        )}
      </Stack>
      <Textarea
        size="md"
        label={t(r('address'))}
        withAsterisk
        autosize
        minRows={2}
        autoComplete="street-address"
        value={values.address}
        error={errors.address}
        onChange={(e) => onChange({ address: e.currentTarget.value })}
      />
      <AreaInput
        size="md"
        label={t('form.area')}
        placeholder={t('form.areaHint')}
        withAsterisk
        value={values.area}
        error={errors.area}
        onChange={(area) => onChange({ area })}
      />
      <Button size="lg" radius="xl" leftSection={<IconEye size={20} />} onClick={onReview}>
        {t(r('review'))}
      </Button>
    </section>
  )
}

/** The receipt as it will print, ticked as the payer's and issued; or back to the form */
function ReviewCard({ name, sending, onEdit, onIssue }: { name: string; sending: boolean; onEdit: () => void; onIssue: () => void }) {
  const { t } = useTranslation()
  const [confirmed, setConfirmed] = useState(false)
  const [missing, setMissing] = useState(false)
  return (
    <section className={classes.card}>
      <Stack gap={4}>
        <h2 className={classes.cardTitle}>{t(r('reviewTitle'))}</h2>
        <Text size="sm" c="dimmed">
          {t(r('reviewHint'))}
        </Text>
      </Stack>
      <Checkbox
        size="md"
        checked={confirmed}
        error={missing ? t(r('needConfirm')) : undefined}
        label={t(r('confirm'), { name })}
        onChange={(e) => {
          setConfirmed(e.currentTarget.checked)
          setMissing(false)
        }}
      />
      <Group gap="sm" grow>
        <Button size="lg" radius="xl" variant="default" leftSection={<IconPencil size={20} />} onClick={onEdit} disabled={sending}>
          {t(r('edit'))}
        </Button>
        <Button
          size="lg"
          radius="xl"
          leftSection={<IconReceipt size={20} />}
          loading={sending}
          onClick={() => (confirmed ? onIssue() : setMissing(true))}
        >
          {t(r('issue'))}
        </Button>
      </Group>
    </section>
  )
}

/** Why it does not open here, and the branch to call */
function Closed({ data, icon, title, body }: { data: SelfReceiptPage; icon: typeof IconFileOff; title: string; body: string }) {
  return (
    <section className={classes.card}>
      <EmptyState icon={icon} title={title} body={body} actions={<CallBranch data={data} />} />
    </section>
  )
}

function ReceiptView({ code, id, data }: { code: string; id: string; data: SelfReceiptPage }) {
  const { t, i18n } = useTranslation()
  const locale = currentLocale(i18n)
  const queries = useQueryClient()
  const client = useMemo(() => answered(data), [data])
  const sheet = useRef<HTMLDivElement>(null)
  const [back] = useState(() => trackPage(code))
  const company = data.company?.name ?? ''
  const issued = data.state === 'issued' && !!data.record
  const docName = t('accounting:customerReceipt.documentTitle')
  const printer = usePrint({ title: `${docName} ${data.number ?? ''}`, pageLabel: t('print.page') })
  const [values, setValues] = useState(() => initial(data.defaults))
  const [errors, setErrors] = useState<Errors>({})
  const [step, setStep] = useState<'form' | 'review'>('form')
  const [sending, setSending] = useState(false)
  const [making, setMaking] = useState(false)
  useDocumentTitle(`${issued ? `${docName} ${data.number}` : t(r('title'))} · ${company}`)

  const change = (next: Partial<Values>) => {
    setValues((current) => ({ ...current, ...next }))
    setErrors((current) => ({
      ...current,
      ...Object.fromEntries(Object.keys(next).map((key) => [key === 'headOffice' || key === 'branchNo' ? 'branch' : key, undefined])),
    }))
  }
  const review = () => {
    const checked = selfReceiptInput.safeParse(inputOf(values))
    if (!checked.success) return setErrors(errorsOf(checked.error.issues))
    setStep('review')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
  const issue = async () => {
    setSending(true)
    try {
      const page = await receiptApi.issue(code, id, inputOf(values) as SelfReceiptInput)
      queries.setQueryData(['public', 'receipt', code, id], page)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (error) {
      if (isApiError(error) && error.issues?.length) {
        setErrors(errorsOf(error.issues))
        setStep('form')
      } else {
        notifications.show({ color: 'red', message: error instanceof Error ? error.message : String(error) })
        // Issued meanwhile, or no longer open: the page as it stands now
        if (isApiError(error, 'CONFLICT')) void queries.invalidateQueries({ queryKey: ['public', 'receipt', code, id] })
      }
    } finally {
      setSending(false)
    }
  }
  const download = async () => {
    if (!sheet.current) return
    setMaking(true)
    try {
      await savePdf(sheet.current, data.number ?? docName, t('print.page'))
    } catch {
      notifications.show({ color: 'red', message: t('sign:failed') })
    } finally {
      setMaking(false)
    }
  }

  const input = inputOf(values)
  const shown = issued ? data.record : step === 'review' && data.record ? { ...data.record, ...input } : null
  const date = (day: string) => formatDate(day, { locale, style: 'long' })
  let body: ReactNode
  if (data.state === 'issued' && !issued) {
    body = <Closed data={data} icon={IconReceipt} title={t(r('elsewhere'))} body={t(r('elsewhereHint'), { number: data.number })} />
  } else if (data.state === 'expired') {
    body = <Closed data={data} icon={IconClockOff} title={t(r('expired'))} body={t(r('expiredHint'), { date: date(data.until) })} />
  } else if (data.state === 'office') {
    body = <Closed data={data} icon={IconBuildingStore} title={t(r('office'))} body={t(r('officeHint'))} />
  } else if (issued) {
    body = (
      <section className={classes.card}>
        <div className={classes.done}>
          <span className={classes.doneIcon}>
            <IconCheck size={24} />
          </span>
          <Stack gap={2}>
            <Text fw={500}>{t(r('issued'))}</Text>
            <Text size="sm" c="dimmed" className="tnn-num">
              {t(r('number'), { number: data.number })}
            </Text>
          </Stack>
        </div>
        <Group gap="sm">
          <Button size="md" radius="xl" leftSection={<IconDownload size={18} />} loading={making} onClick={download}>
            {making ? t('sign:making') : t('sign:download')}
          </Button>
          <Button size="md" radius="xl" variant="default" leftSection={<IconPrinter size={18} />} onClick={() => printer.print()}>
            {t('sign:print')}
          </Button>
        </Group>
        <Text className={classes.legal}>{t(r('keep'))}</Text>
      </section>
    )
  } else if (step === 'review') {
    body = <ReviewCard name={input.name} sending={sending} onEdit={() => setStep('form')} onIssue={issue} />
  } else {
    body = (
      <>
        <ReceiptForm values={values} onChange={change} errors={errors} onReview={review} />
        <Text className={classes.legal}>
          {t(r('until'), { date: date(data.until) })} · {t(r('person'))}
        </Text>
      </>
    )
  }

  return (
    <div className={classes.page}>
      <header className={classes.top}>
        <InitialsAvatar name={company} size={36} />
        <span className={classes.company}>{company}</span>
      </header>
      <div className={classes.column}>
        <div className={classes.hero}>
          {back && (
            <Anchor href={back} size="sm" className={classes.back}>
              <IconArrowLeft size={16} />
              {t(r('back'))}
            </Anchor>
          )}
          <Stack gap={2}>
            <h1 className={classes.docTitle}>{issued ? docName : t(r('title'))}</h1>
            <Text size="md" fw={500} className="tnn-num">
              {issued ? data.number : t(r('bill'), { number: data.bill.number })}
            </Text>
          </Stack>
          <div className={classes.facts}>
            <span className={classes.amount}>{t(r('paid'), { amount: formatMoney(data.received) })}</span>
            <span>{t(r('paidAt'), { date: date(data.paidOn), branch: data.branch.name })}</span>
          </div>
        </div>
        {body}
        {shown && (
          <QueryClientProvider client={client}>
            <ScaledPage sheet={sheet} printRef={printer.ref}>
              <CustomerReceiptDocument record={shown} refs={data.refs} />
            </ScaledPage>
          </QueryClientProvider>
        )}
        {shown && <Text className={classes.legal}>{t(r('legal'))}</Text>}
      </div>
    </div>
  )
}

/** `/receipt/<code>/<payment>`: the payment's page, or why it does not open */
export function SelfReceiptPageView({ code, id }: { code: string; id: string }) {
  const { t } = useTranslation()
  const query = useQuery({ queryKey: ['public', 'receipt', code, id], queryFn: () => receiptApi.page(code, id), enabled: !!code && !!id })
  if (query.data) return <ReceiptView code={code} id={id} data={query.data} />
  const missing = !code || !id || (isApiError(query.error) && ['NOT_FOUND', 'TRACK_NOT_FOUND'].includes(query.error.code))
  return (
    <div className={classes.page}>
      <div className={classes.state}>
        {!missing && query.isPending ? (
          <Loader type="dots" />
        ) : (
          <EmptyState
            icon={IconFileOff}
            title={t(missing ? r('missing') : 'sign:failed')}
            body={missing ? t(r('missingHint')) : undefined}
            actions={
              missing ? undefined : (
                <Button variant="light" onClick={() => query.refetch()}>
                  {t('actions.retry')}
                </Button>
              )
            }
          />
        )}
      </div>
    </div>
  )
}
